import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  input,
  output,
  inject,
} from '@angular/core';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  FileSaverService,
  ImageThumbnailUnsupportedComponent,
  PrimengButtonDirective,
  SpinnerFilledComponent,
  ToastService,
} from '@web/shared/ui';
import { Guid, once, shareReplayOnce } from '@web/utils';
import { AdminMultimediaDetailsForm } from '../../models/admin-multimedia-details-form';
import { AdminMultimediaDetails } from '../../models/admin-multimedia-details';
import { AdminMultimediaDataSourceService } from '../../services/admin-multimedia-data-source.service';
import { MultimediaImagePreviewComponent } from '../../components/multimedia-image-preview/multimedia-image-preview.component';
import { AsyncPipe } from '@angular/common';
import { MultimediaAttributesComponent } from '../../components/multimedia-attributes/multimedia-attributes.component';
import { DividerModule } from 'primeng/divider';
import { MultimediaDetailsFormComponent } from '../../components/multimedia-details-form/multimedia-details-form.component';
import { Button } from 'primeng/button';
import { Clipboard } from '@angular/cdk/clipboard';
import { MultimediaDocumentPreviewComponent } from '../../components/multimedia-document-preview/multimedia-document-preview.component';
import { MultimediaArchivePreviewComponent } from '../../components/multimedia-archive-preview/multimedia-archive-preview.component';

@Component({
  selector: 'app-multimedia-details',
  imports: [
    AsyncPipe,
    Button,
    DividerModule,
    MultimediaAttributesComponent,
    MultimediaDetailsFormComponent,
    MultimediaImagePreviewComponent,
    PrimengButtonDirective,
    SpinnerFilledComponent,
    ImageThumbnailUnsupportedComponent,
    MultimediaDocumentPreviewComponent,
    MultimediaArchivePreviewComponent,
  ],
  templateUrl: './multimedia-details.component.html',
  styleUrl: './multimedia-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaDetailsComponent {
  private readonly clipboard = inject(Clipboard);
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly fileSaver = inject(FileSaverService);
  private readonly toast = inject(ToastService);

  public readonly id = input.required<Guid>();
  public readonly id$ = toObservable(this.id);
  public readonly removeSuccessEvent = output<void>();

  protected multimedia$ = this.multimediaSource();

  public handleSubmitClickEvent(form: AdminMultimediaDetailsForm): void {
    this.multimediaDataSource
      .update(this.id(), form)
      .pipe(once())
      .subscribe(() => {
        this.refresh();
        this.detectChanges();
      });
  }

  public handleRemoveClick(): void {
    this.multimediaDataSource
      .confirmAndRemoveById(this.id())
      .pipe(once())
      .subscribe({
        next: () => this.removeSuccessEvent.emit(),
      });
  }

  public handleDownloadClick(multimedia: AdminMultimediaDetails): void {
    this.fileSaver
      .saveAsFromLink(multimedia.downloadUrl, multimedia.filename)
      .pipe(once())
      .subscribe(() => {
        this.toast.show((builder) =>
          builder.initSuccess('Plik został pobrany.').build(),
        );
      });
  }

  public handleCopyLinkClick(multimedia: AdminMultimediaDetails): void {
    this.clipboard.copy(multimedia.previewUrl);

    this.toast.show((builder) =>
      builder.initSuccess('Skopiowano adres URL.').build(),
    );
  }

  private detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }

  private multimediaSource(): Observable<{
    details: AdminMultimediaDetails;
    form: AdminMultimediaDetailsForm;
  }> {
    return this.id$.pipe(
      switchMap((id) =>
        forkJoin({
          details: this.multimediaDataSource.fetchDetailsById(id),
          form: this.multimediaDataSource.fetchDetailsFormById(id),
        }),
      ),
      shareReplayOnce(),
    );
  }

  private refresh(): void {
    this.multimedia$ = this.multimediaSource();
  }
}
